/**
 * Admin: How Event Finder Works
 *
 * Plain-language reference for the two pipeline jobs. Mirrors docs/HOW_IT_WORKS.md.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Database, Globe, Play, Search, Sparkles } from 'lucide-react';
import styled from 'styled-components';
import { AdminLayout } from '../components/AdminLayout';
import {
  Card,
  CardTitle,
  CardDescription,
  Banner,
  SampleTable,
} from '../components/admin/AdminUI';
import { colors, spacing, typography, borderRadius } from '../styles/theme';

const Prose = styled.div`
  color: ${colors.text.secondary};
  font-size: ${typography.fontSizes.sm};
  line-height: 1.65;

  p {
    margin: 0 0 ${spacing[3]};
  }

  strong {
    color: ${colors.text.primary};
    font-weight: ${typography.fontWeights.semibold};
  }

  ul,
  ol {
    margin: 0 0 ${spacing[3]};
    padding-left: ${spacing[5]};
  }

  li {
    margin-bottom: ${spacing[2]};
  }

  code {
    background: ${colors.background.secondary};
    border-radius: ${borderRadius.sm};
    padding: 1px 5px;
    font-family: ${typography.fontFamilies.mono};
    font-size: 0.9em;
    color: ${colors.text.primary};
  }
`;

const StepHeading = styled.h3`
  margin: ${spacing[5]} 0 ${spacing[2]};
  font-size: ${typography.fontSizes.base};
  font-weight: ${typography.fontWeights.semibold};
  color: ${colors.text.primary};
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
`;

const Flow = styled.pre`
  background: ${colors.background.secondary};
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.md};
  padding: ${spacing[4]};
  overflow-x: auto;
  font-family: ${typography.fontFamilies.mono};
  font-size: ${typography.fontSizes.xs};
  line-height: 1.5;
  color: ${colors.text.primary};
  margin: 0 0 ${spacing[4]};
`;

const JobBadge = styled.span<{ tone: 'discovery' | 'extraction' }>`
  display: inline-block;
  padding: 2px ${spacing[2]};
  border-radius: ${borderRadius.full};
  font-size: ${typography.fontSizes.xs};
  font-weight: ${typography.fontWeights.semibold};
  background: ${({ tone }) =>
    tone === 'discovery' ? colors.primary[50] : colors.success[50]};
  color: ${({ tone }) =>
    tone === 'discovery' ? colors.primary[700] : colors.success[700]};
`;

export const AdminHowItWorksPage: React.FC = () => {
  return (
    <AdminLayout
      currentPage="howItWorks"
      pageTitle="How Event Finder Works"
      pageDescription="A plain-language guide to the two jobs that keep the site full of events."
    >
      <Card>
        <CardTitle>The one thing to remember</CardTitle>
        <Prose>
          <p>
            <strong>Job 1 finds websites that list events.</strong>{' '}
            <strong>Job 2 reads those websites and pulls out the actual events.</strong>
          </p>
          <p>
            Job 1 is <JobBadge tone="discovery">Refresh Event Sources with WebIQ</JobBadge>.
            Job 2 is <JobBadge tone="extraction">Extract Events from Web Sources with AI</JobBadge>.
          </p>
        </Prose>
        <Flow>{`Entity  ──has──▶  Event Sources  ──produce──▶  Events
(Contoso)         (contoso.com/events)          (Contoso Summit 2026)`}</Flow>
      </Card>

      <Card>
        <CardTitle>
          <Database size={18} style={{ verticalAlign: '-3px', marginRight: 6 }} />
          The three things we store
        </CardTitle>
        <CardDescription>
          Everything is built on these three ideas. Get these and the rest follows.
        </CardDescription>
        <SampleTable>
          <thead>
            <tr>
              <th>Thing</th>
              <th>Plain English</th>
              <th>Example</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Entity</strong></td>
              <td>An organization, corporation, tech company, university, or city we care about.</td>
              <td>Contoso Ltd</td>
            </tr>
            <tr>
              <td><strong>Event Source</strong></td>
              <td>
                A web page that lists events. Not one event &mdash; a page that lists
                many, over and over.
              </td>
              <td>contoso.com/events</td>
            </tr>
            <tr>
              <td><strong>Event</strong></td>
              <td>One actual event with a title, date, and place.</td>
              <td>Contoso Summit 2026</td>
            </tr>
          </tbody>
        </SampleTable>
        <Prose>
          <p>
            An Event Source is a <strong>reusable listing page</strong>, not a one-off
            event page. That matters: we want pages worth re-reading regularly,
            because next week they will list next week&apos;s events.
          </p>
        </Prose>
      </Card>

      <Card>
        <CardTitle>
          <Globe size={18} style={{ verticalAlign: '-3px', marginRight: 6 }} />
          Job 1 &mdash; Refresh Event Sources with WebIQ
        </CardTitle>
        <CardDescription>
          Answers: &ldquo;Which websites list events for this organization?&rdquo;
        </CardDescription>
        <Banner tone="info">
          This job creates <strong>no events</strong>. It only builds the list of
          websites worth reading later.
        </Banner>
        <Prose>
          <StepHeading><Play size={16} /> Step by step</StepHeading>
          <ol>
            <li>
              <strong>You pick a scope</strong> &mdash; everything, certain entity types
              (Tech Company, Corporation, Organization, Industry, City, University), or specific entity IDs.
            </li>
            <li>
              <strong>The request returns immediately</strong> with a{' '}
              <code>processId</code>. The real work happens in the background, so your
              browser is never left hanging.
            </li>
            <li>
              <strong>A dispatcher walks the entity list</strong> and creates one
              discovery run per entity. One entity failing never stops the others.
            </li>
            <li>
              <strong>For each entity, a worker:</strong>
              <ul>
                <li>
                  <strong>Makes a good search name.</strong> &ldquo;Contoso Holdings
                  Ltd.&rdquo; is a bad search term; a small AI call produces
                  &ldquo;Contoso&rdquo; and we save it.
                </li>
                <li>
                  <strong>Writes several search queries</strong> using the name,
                  aliases, website domain, type, and city. One query is not enough.
                </li>
                <li><strong>Asks WebIQ</strong> to run each query.</li>
                <li><strong>Cleans up results</strong> &mdash; normalizes URLs, drops duplicates.</li>
                <li>
                  <strong>Checks each candidate page</strong> by fetching it and asking:
                  is this a reusable page listing multiple events? Single event pages
                  and blog posts get rejected here.
                </li>
                <li>
                  <strong>Scores how related the page is</strong> to the entity, 0&ndash;100:
                  above 75 is confirmed; 50&ndash;75 goes to a second independent AI judge;
                  below 50 is saved as &ldquo;unlikely&rdquo; and left switched off.
                </li>
                <li><strong>Turns on the winners</strong> as active event sources.</li>
                <li>
                  <strong>Stops there.</strong> The source is now known, but it is not
                  extracted tonight &mdash; Job 2 picks it up on the next night scheduled
                  for that entity type. The two jobs are deliberately decoupled.
                </li>
              </ul>
            </li>
          </ol>
          <p>
            <strong>Why Job 1 does not just run Job 2 for you.</strong> It used to, and a
            refresh night could never finish: one run promoted 2,808 sources while its own
            searches were still only 647 of 1,980 done at the 60-minute mark, so the night
            was marked failed twice &mdash; once for the real overrun, and once for an
            extraction queue it was never scheduled to drain. The hand-off now sits behind{' '}
            <code>WEBIQ_TRIGGER_EXTRACTION</code>, which is <strong>off by default</strong>.
            One job runs per night and each gets the whole night.
          </p>
          <p>
            <strong>Your manual decisions win.</strong> If you manually approved or
            rejected a source, this job never silently flips it back. And missing once
            does not mean deleted &mdash; sites move and search results wobble.
          </p>
        </Prose>
        <Link to="/admin/refresh-event-sources">Go to Refresh Event Sources &rarr;</Link>
      </Card>

      <Card>
        <CardTitle>
          <Sparkles size={18} style={{ verticalAlign: '-3px', marginRight: 6 }} />
          Job 2 &mdash; Extract Events from Web Sources with AI
        </CardTitle>
        <CardDescription>
          Answers: &ldquo;What events are actually listed on these pages right now?&rdquo;
        </CardDescription>
        <Prose>
          <StepHeading><Play size={16} /> Step by step</StepHeading>
          <ol>
            <li>
              <strong>You pick a scope</strong> &mdash; every active source, or just one
              entity, one source, or one source type.
            </li>
            <li>
              <strong>Matching sources go on a work queue.</strong> The response tells
              you how many were queued.
            </li>
            <li>
              <strong>For each source, a worker:</strong>
              <ul>
                <li><strong>Checks meaningful page changes</strong> with Jina Reader text before spending AI tokens.</li>
                <li><strong>Reads robust text first</strong>, falls back to the local rendered DOM, and uses screenshot AI only when text is unusable or strong event signals remain unexplained.</li>
                <li>
                  <strong>Asks AI to pull out events</strong> &mdash; title, dates, venue,
                  price, topics, speakers.
                </li>
                <li>
                  <strong>Decides if each event is new.</strong> The same conference is
                  often listed on five sites with slightly different titles, so we
                  compare with vector similarity plus an AI judge. That way
                  &ldquo;KubeCon NA 2026&rdquo; and &ldquo;KubeCon North America&rdquo;
                  land on one event.
                </li>
                <li><strong>Saves it</strong>, recording which source it came from.</li>
                <li><strong>Fills in map coordinates</strong>, then writes to the search index.</li>
                <li><strong>Records the outcome</strong> so you can audit what happened.</li>
              </ul>
            </li>
          </ol>
          <p>
            Two records worth knowing: <code>EventInfoMap</code> is the{' '}
            <strong>current</strong> answer (&ldquo;source X currently lists event
            Y&rdquo;), and <code>EventSourceEventObservations</code> is the{' '}
            <strong>history</strong> of every time we ever saw it. The history is
            append-only, so nothing is lost.
          </p>
          <p>
            Some failures are normal and permanent &mdash; sites go down, block bots, or
            hide events behind JavaScript we cannot read.
          </p>
        </Prose>
        <Link to="/admin/extract-events">Go to Extract Events &rarr;</Link>
      </Card>

      <Card>
        <CardTitle>How the two jobs fit together</CardTitle>
        <Flow>{`┌─────────────────────────────────────────────┐
│  Job 1: Refresh Event Sources with WebIQ    │
│  "which websites list events?"              │
│  Tech Company Sun · Corporation Tue · City Sat UTC │
└───────────────────┬─────────────────────────┘
                    │ accepted sources are activated,
                    │ then wait for their next extraction night
                    ▼
┌─────────────────────────────────────────────┐
│  Job 2: Extract Events from Web Sources     │
│  "what events are on those websites?"       │
│  UTC Mon-Sat, one entity type per night     │
└───────────────────┬─────────────────────────┘
                    │ new + updated events
                    ▼
      Search index  +  Cache  →  the public site`}</Flow>
        <Prose>
          <p>
            <strong>Discovery and extraction stay in separate windows.</strong>{' '}
            <code>job-pef-nightly</code> runs extraction at 08:00 UTC Monday through
            Saturday and Tech Company WebIQ Sunday. <code>job-pef-webiq</code> runs Corporation
            WebIQ Tuesday and City WebIQ Saturday at 00:15 UTC, just after the daily
            provider quota resets and nearly eight hours before extraction. Accepted
            sources wait for the next extraction window for their entity type.
          </p>
        </Prose>
        <SampleTable>
          <thead>
            <tr>
              <th>Situation</th>
              <th>Run this</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Added new entities, or want to find new websites</td>
              <td><strong>Job 1</strong>, then <strong>Job 2</strong> to extract what it found</td>
            </tr>
            <tr>
              <td>Just want today&apos;s events from sites we already have</td>
              <td><strong>Job 2</strong></td>
            </tr>
            <tr>
              <td>Search results look stale or wrong</td>
              <td>Sync Search Index</td>
            </tr>
            <tr>
              <td>Site counts look wrong</td>
              <td>Rebuild Cache</td>
            </tr>
          </tbody>
        </SampleTable>
      </Card>

      <Card>
        <CardTitle>
          <Search size={18} style={{ verticalAlign: '-3px', marginRight: 6 }} />
          Where things are recorded
        </CardTitle>
        <SampleTable>
          <thead>
            <tr>
              <th>Table</th>
              <th>Plain English</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><code>Entities</code></td><td>The organizations, corporations, universities, and cities we track.</td></tr>
            <tr><td><code>EventSources</code></td><td>The websites themselves, one row per URL.</td></tr>
            <tr>
              <td><code>EntityEventSources</code></td>
              <td>
                &ldquo;This website lists events for this entity.&rdquo; The
                authoritative link, including active state and confidence.
              </td>
            </tr>
            <tr><td><code>EventInfo</code></td><td>The events. One row per real-world event.</td></tr>
            <tr><td><code>EventInfoMap</code></td><td>Which source currently lists which event.</td></tr>
            <tr>
              <td><code>EventSourceEventObservations</code></td>
              <td>Full history of every sighting. Never deleted.</td>
            </tr>
            <tr>
              <td><code>WebsiteDiscoveryBatches</code> / <code>WebsiteDiscoveryRuns</code></td>
              <td>Progress tracking for Job 1. One batch per request, one run per entity.</td>
            </tr>
            <tr>
              <td><code>WorkflowMetadata</code></td>
              <td>Progress tracking for Job 2. One row per source processed.</td>
            </tr>
            <tr>
              <td><code>NightlyRuns</code> / <code>NightlyRunSamples</code></td>
              <td>Nightly job history and roughly 10-minute operational samples shown on <code>/admin/nightly-runs</code>.</td>
            </tr>
          </tbody>
        </SampleTable>
      </Card>

      <Card>
        <CardTitle>Name glossary</CardTitle>
        <CardDescription>
          Friendly names in this portal map to these technical names in code and logs.
          The endpoint names are unchanged because scheduled automation depends on them.
        </CardDescription>
        <SampleTable>
          <thead>
            <tr>
              <th>Friendly name</th>
              <th>HTTP endpoint</th>
              <th>Queues</th>
              <th>Progress table</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Refresh Event Sources with WebIQ</strong></td>
              <td><code>refreshAndProcessEventSources</code></td>
              <td><code>website-discovery-dispatch</code> &rarr; <code>website-discovery</code></td>
              <td><code>WebsiteDiscoveryRuns</code></td>
            </tr>
            <tr>
              <td><strong>Extract Events from Web Sources with AI</strong></td>
              <td><code>startQueueBasedProcessing</code></td>
              <td><code>partner-processing</code></td>
              <td><code>WorkflowMetadata</code></td>
            </tr>
          </tbody>
        </SampleTable>
      </Card>

      <Card>
        <CardTitle>Troubleshooting</CardTitle>
        <Prose>
          <ul>
            <li>
              <strong>Job 1 fails immediately with an authorization error.</strong>{' '}
              WebIQ access is not working. Production authenticates with the Function
              App&apos;s managed identity, and that identity must be authorized on the
              WebIQ side.
            </li>
            <li>
              <strong>Job 2 queue is not going down.</strong> Check the Function App is
              running and scaled up. Each message fetches a page and makes AI calls, so
              it is never instant.
            </li>
            <li>
              <strong>The site does not show events that are in the database.</strong>{' '}
              The search index or cache is behind. Run Sync Search Index, then Rebuild
              Cache.
            </li>
            <li>
              <strong>The same event appears twice.</strong> Deduplication missed a
              pair. Use Duplicate Events, which merges the records and cleans the
              search index.
            </li>
          </ul>
        </Prose>
      </Card>
    </AdminLayout>
  );
};

export default AdminHowItWorksPage;
